import type { EditorState } from "prosemirror-state";
import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

export type EditorSuggestionType = "mention" | "slash";

export type EditorSuggestionState = {
  query: string;
  range: {
    from: number;
    to: number;
  };
  trigger?: "@" | "$" | "/";
  type: EditorSuggestionType;
} | null;

export const editorSuggestionsKey = new PluginKey<EditorSuggestionState>(
  "editor-v3-suggestions",
);

const SUGGESTION_KEYS = new Set([
  "ArrowDown",
  "ArrowUp",
  "Enter",
  "Escape",
  "Tab",
]);

const isSelectionInCodeBlock = (state: EditorState) => {
  const { $from } = state.selection;

  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.spec.code) {
      return true;
    }
  }

  return false;
};

const findSuggestion = (state: EditorState): EditorSuggestionState => {
  const { selection } = state;

  if (!selection.empty || isSelectionInCodeBlock(state)) {
    return null;
  }

  const { $from } = selection;
  const textBefore = $from.parent.textBetween(0, $from.parentOffset);

  const slashMatch = /(?:^|\s)\/([A-Za-z0-9_-]*)$/.exec(textBefore);
  if (slashMatch) {
    const query = slashMatch[1] ?? "";

    return {
      query,
      range: {
        from: $from.pos - query.length - 1,
        to: $from.pos,
      },
      trigger: "/",
      type: "slash",
    };
  }

  const mentionMatch = /(?:^|\s)([@$])([A-Za-z0-9_]{0,26})$/.exec(textBefore);
  if (mentionMatch) {
    const trigger = mentionMatch[1] === "$" ? "$" : "@";
    const query = mentionMatch[2] ?? "";

    return {
      query,
      range: {
        from: $from.pos - query.length - 1,
        to: $from.pos,
      },
      trigger,
      type: "mention",
    };
  }

  return null;
};

const equalSuggestions = (
  left: EditorSuggestionState,
  right: EditorSuggestionState,
) => {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.type === right.type &&
    left.trigger === right.trigger &&
    left.query === right.query &&
    left.range.from === right.range.from &&
    left.range.to === right.range.to
  );
};

export const closeEditorSuggestion = (view: EditorView) => {
  view.dispatch(view.state.tr.setMeta(editorSuggestionsKey, { close: true }));
};

export const editorSuggestionsPlugin = ({
  onChange,
  onKeyDown,
}: {
  onChange?: (state: EditorSuggestionState, view: EditorView) => void;
  onKeyDown?: (
    event: KeyboardEvent,
    state: NonNullable<EditorSuggestionState>,
    view: EditorView,
  ) => boolean;
} = {}) =>
  new Plugin<EditorSuggestionState>({
    key: editorSuggestionsKey,
    props: {
      handleKeyDown(view, event) {
        const suggestion = editorSuggestionsKey.getState(view.state);

        if (!suggestion || !SUGGESTION_KEYS.has(event.key)) {
          return false;
        }

        return onKeyDown?.(event, suggestion, view) ?? false;
      },
    },
    state: {
      apply(transaction, previous, _oldState, newState) {
        const meta = transaction.getMeta(editorSuggestionsKey);

        if (meta?.close) {
          return null;
        }

        if (!transaction.docChanged && !transaction.selectionSet) {
          return previous;
        }

        return findSuggestion(newState);
      },
      init(_config, state) {
        return findSuggestion(state);
      },
    },
    view(view) {
      let previous = editorSuggestionsKey.getState(view.state) ?? null;
      onChange?.(previous, view);

      return {
        destroy() {
          onChange?.(null, view);
        },
        update(nextView) {
          const next = editorSuggestionsKey.getState(nextView.state) ?? null;

          if (equalSuggestions(previous, next)) {
            return;
          }

          previous = next;
          onChange?.(next, nextView);
        },
      };
    },
  });
